import {Page} from '../../../utils/page';
import {ProductSearchRequestV2} from "../../../models/quote/product-search-request-v2";
import {ProductWithChildrenV2} from 'src/app/core/models/productv2/productv2-children.model';
import {StandardProductManagerDatabase} from "../../databases/standard-product-manager.database";
import {EnumQuoteServiceType} from "../../../models/enum/enum-quote-service-type";

export class SqliteProductV2Repository {

    constructor(private readonly standardProductManagerDatabase: StandardProductManagerDatabase) {}


    async searchArticles(
        productSearchRequest: ProductSearchRequestV2,
        pageable: Map<string,string>,
        atomicOnly: boolean
    ): Promise<Page<ProductWithChildrenV2>> {
        const page = pageable.has('page') ? parseInt(pageable.get('page')!) : 0;
        const size = pageable.has('size') ? parseInt(pageable.get('size')!) : 20;
        const offset = page * size;

        const whereClauses: string[] = [];
        const params: Record<string, any> = {};

        let childrenSelect = `p.children`;

        // Service code
        if (productSearchRequest.serviceCode) {
            whereClauses.push(`p.serviceLine = $serviceCode`);
            params["$serviceCode"] = productSearchRequest.serviceCode;
        }

        if (productSearchRequest.codeOrLabel) {
            params["$codeOrLabel"] = productSearchRequest.codeOrLabel;
            whereClauses.push(`(p.code = $codeOrLabel OR p.label like "%${productSearchRequest.codeOrLabel}%" COLLATE NOCASE)`);
        }

        // Extinction
        if (productSearchRequest.extinctionIncluded === false) {
            whereClauses.push(`(p.status <> 'EXT' OR p.status IS NULL)`);

            childrenSelect = `
                COALESCE(
                    (
                        SELECT json_group_array(child.value)
                        FROM json_each(p.children) AS child
                        WHERE json_extract(child.value, '$.consumable.status') IS NULL
                        OR json_extract(child.value, '$.consumable.status') <> 'EXT'
                    ),
                    '[]'
                )
            `;
        }

        // IN codes
        if (productSearchRequest.elisCodeList?.length > 0) {
            const inParams = productSearchRequest.elisCodeList.map((c, i) => `$c${i}`);
            productSearchRequest.elisCodeList.forEach((c, i) => params[`$c${i}`] = c);
            whereClauses.push(`p.code IN (${inParams.join(',')})`);
        }

        // Atomic only
        if (atomicOnly) {
            whereClauses.push(`p.productType = 'ATOMIC'`);
        }

        // Région (priorisation)
        let regionParam = null;
        if (productSearchRequest.region) {
            regionParam = productSearchRequest.region;
            params["$region"] = regionParam;
            whereClauses.push(`($region IS NULL OR pl.region IN ($region, 'Tous'))`);
        } else {
            params["$region"] = null;
            whereClauses.push(`($region IS NULL OR pl.region IN ('Tous'))`);
        }

        const whereSql = whereClauses.length > 0
            ? `WHERE ${whereClauses.join(' AND ')}`
            : '';

        // Sous-requête avec ROW_NUMBER() pour priorisation régionale
        const subQuery = `
            SELECT
            p.id,
            p.serviceLine,
            p.productLine,
            p.priceListId,
            p.productType,
            p.family,
            p.subfamily,
            p.code,
            p.label,
            p.type,
            p.status,
            p.statusRank,
            p.serviceTimePerDevice,
            p.installationTimePerDevice,
            p.productId,
            ${childrenSelect} AS children,

                ROW_NUMBER() OVER (
                    PARTITION BY p.code
                    ORDER BY
                        CASE
                            WHEN $region IS NULL THEN 0
                            WHEN pl.region = $region THEN 2
                            WHEN pl.region = 'Tous' THEN 1
                            ELSE 0
                        END DESC
                ) AS rn
            FROM product p
            LEFT JOIN productchildren pc ON NULLIF(p.pricelistId, 0) IS NULL
            AND pc.parentId = p.id
            INNER JOIN pricelist pl ON pl.id = COALESCE(NULLIF(p.pricelistId, 0), pc.pricelistId)
            ${whereSql}
        `;

        let orderBy = `ORDER BY label`;
        const sortByStockServiceCodes = [
            EnumQuoteServiceType.LS.valueOf(),
            EnumQuoteServiceType.VT.valueOf()
        ];

        if (sortByStockServiceCodes.includes(productSearchRequest.serviceCode)) {
            orderBy = `ORDER BY statusRank, label`;
        }

        // Requête principale avec pagination
        const listSql = `
            SELECT *
            FROM (
                     ${subQuery}
                     )
            WHERE rn = 1
            ${orderBy}
            LIMIT $size
            OFFSET $offset
        `;
        params["$size"] = size;
        params["$offset"] = offset;

        // Requête de comptage
        const totalSql = `
            SELECT COUNT(distinct code) as total
            FROM (
                ${subQuery}
            )
            WHERE rn = 1
        `;

        // Execute list query
        const pagedDocs :ProductWithChildrenV2[] = await this.standardProductManagerDatabase.queryObjects<ProductWithChildrenV2>(
            listSql,
            params,
            ['children']);

        // Execute count query
        const totalDocumentsCount : number = await this.standardProductManagerDatabase.countWithParams(totalSql, params);

        return {
            totalElements: totalDocumentsCount,
            totalPages: Math.ceil(totalDocumentsCount / size),
            first: page === 0,
            last: (page + 1) * size >= totalDocumentsCount,
            size: size,
            content: pagedDocs,
            number: page,
            numberOfElements: pagedDocs.length,
            empty: pagedDocs.length === 0
        };
    }

}
